import { FC, useRef } from 'react';
import { EmptyDataPlaceholder } from '@/shared/ui/empty-data-placeholder';
import { UITable } from '@/shared/ui/ui-table';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { DealStatusChip } from '../../../shared/deal-status-chip';
import { numericFormatter } from 'react-number-format';
import { Link } from 'react-router-dom';

import ArrowLeftIcon from '@/app/assets/icons/arrow-left.svg?react';
import ChronologyIcon from '@/app/assets/icons/chronology/chronology-icon.svg?react';
import { useTranslation } from 'react-i18next';
import { DealRequest } from '../../../app/api/types';
import { FactorRequestsFilters } from '..';
import { getDealSignatureStatusLabel } from '@/shared/single-deal/lib/get-single-deal-status';
import { getBranchAssignmentStatus } from '@/shared/single-deal/lib';
import { BranchStatusChip } from '@/shared/deal-status-chip/branch-status-chip';
import {
  getDealBuyerName,
  getDealSellerName,
  getInternationalDealKind,
  getInternationalDealLabel,
} from '@/app/helpers';
import { ChronologyModalRef } from '@/shared/modals/chronology-modal/types';
import { ChronologyModal } from '@/shared/modals/chronology-modal';

interface Props {
  data: DealRequest[] | undefined;
  filters: FactorRequestsFilters;
  updateFilters: (filters: Partial<FactorRequestsFilters>) => void;
  isLoading: boolean;
  totalPages: number;
}

export const FactorRequestsBody: FC<Props> = ({
  data,
  filters,
  updateFilters,
  isLoading,
  totalPages,
}) => {
  const { t } = useTranslation();
  const chronologyModalRef = useRef<ChronologyModalRef>(null);

  const backUrl = encodeURIComponent(
    window.location.pathname + window.location.search
  );

  const handleChronologyIconClick = (row: DealRequest) => {
    chronologyModalRef.current?.open({ row, backUrl });
  };

  return (
    <Stack spacing={2.5}>
      <EmptyDataPlaceholder
        isEmpty={!data?.length}
        title={t('no_factor_deals_yet_title')}
        subtitle={t('no_factor_deals_yet_subtitle')}
        isLoading={isLoading}
      >
        <UITable
          data={data ?? []}
          columns={[
            {
              label: t('request_number'),
              key: 'unique',
            },
            {
              label: t('request_date'),
              key: 'createdAt',
            },
            {
              label: t('status'),
              render: (row) => {
                const branchStatus = getBranchAssignmentStatus(row);

                return (
                  <Stack direction='row' alignItems='center' gap={1}>
                    <Tooltip
                      title={t('chronology.title')}
                      placement='bottom'
                      arrow
                      slotProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: '#0B1370',
                            color: '#FFF',
                            fontSize: '1.2rem',
                            fontWeight: 500,
                            borderRadius: '50px',
                          },
                        },
                        arrow: {
                          sx: { color: '#0B1370' },
                        },
                      }}
                    >
                      <IconButton
                        size='small'
                        onClick={() => handleChronologyIconClick(row)}
                      >
                        <ChronologyIcon />
                      </IconButton>
                    </Tooltip>
                    {branchStatus ? (
                      <BranchStatusChip status={branchStatus} />
                    ) : (
                      <DealStatusChip
                        status={row.status}
                        type='factor'
                        label={getDealSignatureStatusLabel(row, 'factor', t)}
                      />
                    )}
                  </Stack>
                );
              },
            },
            {
              label: t('factoring_variant'),
              render: (row) => {
                const internationalKind = getInternationalDealKind(row);

                return (
                  <>
                    {internationalKind ? (
                      <Typography fontSize='inherit'>
                        {getInternationalDealLabel(row, internationalKind, t)}
                      </Typography>
                    ) : (
                      row.factoringType && (
                        <Typography fontSize='inherit'>
                          {t(`factoring_types.${row.factoringType}`)}
                        </Typography>
                      )
                    )}
                    {row.recourseType && (
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        fontSize={12}
                      >
                        {t(`recourse_types.${row.recourseType}`)}
                      </Typography>
                    )}
                  </>
                );
              },
            },
            {
              label: t('supplier'),
              render: (row) => (
                <Stack direction='column'>
                  <Typography fontSize='inherit' color='#0B1370'>
                    {getDealSellerName(row)}
                  </Typography>
                  {['opened', 'closed'].includes(row.factoringType) && (
                    <Typography fontSize='inherit' color='#1555FC'>
                      {t('common:applicant')}
                    </Typography>
                  )}
                </Stack>
              ),
            },
            {
              label: t('buyer'),
              render: (row) => (
                <Stack direction='column'>
                  <Typography fontSize='inherit' color='#0B1370'>
                    {getDealBuyerName(row)}
                  </Typography>
                  {row.factoringType === 'reverse' && (
                    <Typography fontSize='inherit' color='#1555FC'>
                      {t('common:applicant')}
                    </Typography>
                  )}
                </Stack>
              ),
            },
            {
              label: t('deal_sum'),
              render: (row) => (
                <Typography fontWeight={600}>
                  {numericFormatter(String(row.contractAmount), {
                    thousandSeparator: ' ',
                  })}
                </Typography>
              ),
            },
            {
              label: '',
              render: (row) => (
                <Link to={`/factor/requests/${row.unique}?backUrl=${backUrl}`}>
                  <IconButton
                    sx={{
                      rotate: '180deg',
                    }}
                  >
                    <ArrowLeftIcon />
                  </IconButton>
                </Link>
              ),
            },
          ]}
          PaginationProps={{
            count: totalPages || 0,
            page: Number(filters.page),
            onChange: (_e, page) => {
              updateFilters({ page });
            },
            sizes: {
              onRowsPerPageChange: (rowsPerPage) =>
                updateFilters({ pageSize: +rowsPerPage, page: 1 }),
              currentRowsPerPage: String(filters.pageSize),
            },
          }}
        />
      </EmptyDataPlaceholder>

      <ChronologyModal ref={chronologyModalRef} />
    </Stack>
  );
};
